import { useState, useMemo } from 'react'
import { DatePreset } from '../types'

interface DatePickerProps {
  startDate: string
  endDate: string
  onDateChange: (startDate: string, endDate: string) => void
  disabled?: boolean
  compact?: boolean
}

const DATE_PRESETS: DatePreset[] = [
  { label: 'Últimos 7 dias', days: 7 },
  { label: 'Últimos 15 dias', days: 15 },
]

// First month with ROI data
const MIN_YEAR = 2025
const MIN_MONTH = 10 // 0-indexed: November 2025

const MAX_RANGE_MONTHS = 3

function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

/**
 * Para um mês selecionado, retorna o "último dia" a usar como endDate.
 *  - Se for o mês atual, usa o dia de HOJE (não pode ser data futura).
 *  - Caso contrário, usa o último dia do mês.
 *
 * Live precisa do mês corrente porque a janela live-anchored frequentemente
 * inclui hoje (ex.: live ontem + 24h de janela = parte cai hoje).
 */
function getEffectiveEndDay(year: number, month: number): number {
  const today = new Date()
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth()
  return isCurrentMonth ? today.getDate() : getLastDayOfMonth(year, month)
}

type MonthOption = { label: string; value: string; year: number; month: number }

function getAvailableMonths(): MonthOption[] {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() // 0-indexed

  const months: MonthOption[] = []
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]

  // i=0 inclui o mês atual (necessário pra Live, onde a janela de atribuição
  // frequentemente cai dentro do mês corrente). Retroativo até nov/2025.
  for (let i = 0; i <= 24; i++) {
    let m = currentMonth - i
    let y = currentYear
    while (m < 0) {
      m += 12
      y -= 1
    }

    // Stop before November 2025
    if (y < MIN_YEAR || (y === MIN_YEAR && m < MIN_MONTH)) break

    months.push({
      label: `${monthNames[m]} ${y}`,
      value: `${y}-${String(m + 1).padStart(2, '0')}`,
      year: y,
      month: m,
    })
  }

  return months
}

function getMonthValueFromDate(dateStr: string): string {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length < 2) return ''
  return `${parts[0]}-${parts[1]}`
}

function parseMonthValue(value: string): { year: number; month: number } | null {
  const parts = value.split('-')
  if (parts.length < 2) return null
  return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) - 1 }
}

function monthDiff(startYear: number, startMonth: number, endYear: number, endMonth: number): number {
  return (endYear - startYear) * 12 + (endMonth - startMonth)
}

const selectStyle = (isDisabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  color: '#374151',
  background: '#fff',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%236b7280\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: 32,
})

export function DatePicker({ startDate, endDate, onDateChange, disabled = false, compact = false }: DatePickerProps) {
  const [activePreset, setActivePreset] = useState<number | null>(null)

  const availableMonths = useMemo(() => getAvailableMonths(), [])

  const startMonthValue = getMonthValueFromDate(startDate)
  const endMonthValue = getMonthValueFromDate(endDate)

  // Compute allowed end months based on selected start month (max 3 months range)
  const endMonthOptions = useMemo(() => {
    const startParsed = parseMonthValue(startMonthValue)
    if (!startParsed) return availableMonths

    return availableMonths.filter((m) => {
      // End month must be >= start month
      if (m.year < startParsed.year || (m.year === startParsed.year && m.month < startParsed.month)) return false
      // End month must be within MAX_RANGE_MONTHS from start
      const diff = monthDiff(startParsed.year, startParsed.month, m.year, m.month)
      return diff < MAX_RANGE_MONTHS
    })
  }, [startMonthValue, availableMonths])

  const handlePresetClick = (preset: DatePreset, index: number) => {
    // End = hoje (incluindo); start = N dias atrás. Live precisa cobrir
    // compras de hoje, então não dá pra usar yesterday como antes.
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - (preset.days - 1))

    onDateChange(formatDate(start), formatDate(end))
    setActivePreset(index)
  }

  const handleMonthChange = (type: 'start' | 'end', value: string) => {
    setActivePreset(null)
    const [yearStr, monthStr] = value.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1 // 0-indexed

    if (type === 'start') {
      const newStart = `${yearStr}-${monthStr}-01`
      // If current end is before new start or exceeds 3 months, auto-adjust end to same month
      const endParsed = parseMonthValue(getMonthValueFromDate(endDate))
      const needsAdjust = !endParsed
        || (endParsed.year < year || (endParsed.year === year && endParsed.month < month))
        || monthDiff(year, month, endParsed.year, endParsed.month) >= MAX_RANGE_MONTHS

      if (needsAdjust) {
        const lastDay = getEffectiveEndDay(year, month)
        const newEnd = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`
        onDateChange(newStart, newEnd)
      } else {
        onDateChange(newStart, endDate)
      }
    } else {
      const lastDay = getEffectiveEndDay(year, month)
      const newEnd = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`
      onDateChange(startDate, newEnd)
    }
  }

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <select
          value={startMonthValue}
          onChange={(e) => handleMonthChange('start', e.target.value)}
          disabled={disabled}
          style={{ ...selectStyle(disabled), width: 'auto', fontSize: 13, padding: '6px 28px 6px 10px' }}
        >
          <option value="" disabled>Mês início</option>
          {availableMonths.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--ink-400)' }}>→</span>
        <select
          value={endMonthValue}
          onChange={(e) => handleMonthChange('end', e.target.value)}
          disabled={disabled}
          style={{ ...selectStyle(disabled), width: 'auto', fontSize: 13, padding: '6px 28px 6px 10px' }}
        >
          <option value="" disabled>Mês fim</option>
          {endMonthOptions.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="form-group date-picker-wrapper">
      <label>Período</label>
      <div className="date-presets">
        {DATE_PRESETS.map((preset, index) => (
          <button
            key={index}
            className={`preset-btn ${activePreset === index ? 'active' : ''}`}
            onClick={() => handlePresetClick(preset, index)}
            disabled={disabled}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="form-row">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="start-month">Mês Início</label>
          <select
            id="start-month"
            value={startMonthValue}
            onChange={(e) => handleMonthChange('start', e.target.value)}
            disabled={disabled}
            style={selectStyle(disabled)}
          >
            <option value="" disabled>Selecione o mês</option>
            {availableMonths.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="end-month">Mês Fim</label>
          <select
            id="end-month"
            value={endMonthValue}
            onChange={(e) => handleMonthChange('end', e.target.value)}
            disabled={disabled}
            style={selectStyle(disabled)}
          >
            <option value="" disabled>Selecione o mês</option>
            {endMonthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
